/**
 * Ticket Service
 * Wraps ticket API endpoints from api.js
 */

import { ticketsAPI } from './api'

export const ticketService = {
  /**
   * Get all tickets with optional filters
   * @param {Object} filters - { status, priority, assignedTo, limit, offset }
   * @returns {Promise}
   */
  async getTickets(filters = {}) {
    try {
      const response = await ticketsAPI.getAll(filters)
      return response.data
    } catch (error) {
      console.error('Error fetching tickets:', error)
      throw error
    }
  },

  /**
   * Get single ticket by ID
   * @param {string} id - Ticket ID
   * @returns {Promise}
   */
  async getTicketById(id) {
    try {
      const response = await ticketsAPI.getById(id)
      return response.data
    } catch (error) {
      console.error('Error fetching ticket:', error)
      throw error
    }
  },

  /**
   * Create new ticket
   * @param {Object} data - Ticket data { subject, description, channel, priority, etc }
   * @returns {Promise}
   */
  async createTicket(data) {
    try {
      const response = await ticketsAPI.create(data)
      return response.data
    } catch (error) {
      console.error('Error creating ticket:', error)
      throw error
    }
  },

  /**
   * Update existing ticket
   * @param {string} id - Ticket ID
   * @param {Object} updates - Fields to update
   * @returns {Promise}
   */
  async updateTicket(id, updates) {
    try {
      const response = await ticketsAPI.update(id, updates)
      return response.data
    } catch (error) {
      console.error('Error updating ticket:', error)
      throw error
    }
  },

  /**
   * Delete ticket
   * @param {string} id - Ticket ID
   * @returns {Promise}
   */
  async deleteTicket(id) {
    try {
      const response = await ticketsAPI.delete(id)
      return response.data
    } catch (error) {
      console.error('Error deleting ticket:', error)
      throw error
    }
  },

  /**
   * Add message/comment to ticket
   * @param {string} ticketId - Ticket ID
   * @param {string} message - Message text
   * @returns {Promise}
   */
  async addMessage(ticketId, message) {
    try {
      const response = await ticketsAPI.addMessage(ticketId, message)
      return response.data
    } catch (error) {
      console.error('Error adding message:', error)
      throw error
    }
  },

  /**
   * Get all messages for ticket
   * @param {string} ticketId - Ticket ID
   * @returns {Promise}
   */
  async getMessages(ticketId) {
    try {
      const response = await ticketsAPI.getMessages(ticketId)
      return response.data
    } catch (error) {
      console.error('Error fetching messages:', error)
      throw error
    }
  },

  /**
   * Assign ticket to agent
   * @param {string} ticketId - Ticket ID
   * @param {string} agentId - Agent ID
   * @returns {Promise}
   */
  async assignTicket(ticketId, agentId) {
    try {
      const response = await ticketsAPI.assignTicket(ticketId, agentId)
      return response.data
    } catch (error) {
      console.error('Error assigning ticket:', error)
      throw error
    }
  },

  /**
   * Escalate ticket
   * @param {string} ticketId - Ticket ID
   * @param {string} reason - Escalation reason
   * @returns {Promise}
   */
  async escalateTicket(ticketId, reason) {
    try {
      const response = await ticketsAPI.escalate(ticketId, reason)
      return response.data
    } catch (error) {
      console.error('Error escalating ticket:', error)
      throw error
    }
  },
}
